/**
 * Sequenciamento de motoristas da Roteirização (lado da tela).
 *
 * O cálculo é do worker ROTEIRIZACAO_MOTORISTAS (monitor/scheduler/pythonScripts/verso/motoristas):
 * aqui ficam os defaults dos parâmetros, a montagem do corpo do job a partir das rotas da tela e
 * a exportação xlsx das abas Seq_Diario_Motoristas e Motoristas_por_Dia.
 *
 * Regras fixas do lado do worker: o dia operacional começa às 03:00 e o motorista não troca de
 * modal (só de veículo do mesmo tipo, ao entrar num turno com `trocaVeiculo` ligado). A troca entre blocos usa o
 * tempo mínimo de transferência da roteirização (o mesmo do reuso de veículos) quando fim e início
 * ficam no mesmo local; senão, o deslocamento.
 *
 * Jornada: total (8h) = pré + jornada líquida (7h) + pós; a líquida soma transferências,
 * deslocamentos e rotas principais (a espera fica fora dela, mas dentro do total). O almoço é
 * exigido de quem trabalha em turnos de manhã e de tarde (dos dois lados de 12:00).
 */

export const JORNADA_LIQUIDA_PADRAO_MIN = 420;

/** Origem dos turnos dos motoristas exibida na tela. */
export const DRIVER_CYCLES_SOURCE = {
    UNIT: 'turnos_unidade',
    GENERAL: 'turnos_gerais',
    DEFAULT: 'padrao',
    HISTORY: 'historico',
};

/**
 * Turnos usados quando não há turnos_funcionarios cadastrados (ciclos AM1, AM2/PM e SD da especificação).
 * `trocaVeiculo`: quem entra no turno vindo de outro pode trocar de veículo (mesmo tipo|capacidade).
 */
export function defaultDriverCycles() {
    return [
        { ciclo: 'AM1', inicio: '04:00', fim: '08:59', trocaVeiculo: true },
        { ciclo: 'AM2PM', inicio: '09:00', fim: '17:59', trocaVeiculo: true },
        { ciclo: 'SD', inicio: '18:00', fim: '03:59', trocaVeiculo: true },
    ];
}

export function defaultDriverParams() {
    return {
        preJornadaMin: 30,
        posJornadaMin: 30,
        almocoMin: 60,
        aplicarAlmoco: true,
        // Turnos dos motoristas: turnos_funcionarios da unidade do destino ou os turnos gerais.
        ciclos: defaultDriverCycles(),
        // Jornada líquida por tipo de veículo da frota (typeKey do spec); vazio = catálogo/420.
        jornadaPorTipo: {},
    };
}

/** Campos de versões anteriores que saíram da tela (não vão mais ao worker). */
const LEGACY_KEYS = [
    'almocoExigidoAcimaDeMin', 'transferenciaHubMin', 'inicioDiaOperacional', 'multimodalPorCiclo',
    'integrarTransferencias', 'trocaVeiculoEntreTurnos',
];

const WEEKDAYS = ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'];

function hhmm(raw) {
    const m = String(raw == null ? '' : raw).trim().match(/^(\d{1,2}):(\d{2})/);
    return m ? `${m[1].padStart(2, '0')}:${m[2]}` : null;
}

/**
 * Turnos dos motoristas a partir de turnos_funcionarios (getturnosfuncionariosporcerca).
 * O cadastro tem horário por dia da semana e a roteirização é de um dia padrão: vale o
 * primeiro dia preenchido, de segunda a domingo. Turno sem horário fica de fora.
 */
export function cyclesFromEmployeeShifts(turnos) {
    const out = [];
    const seen = new Set();
    (Array.isArray(turnos) ? turnos : []).forEach((t) => {
        if (!t) return;
        const name = String(t.cod_turno_funcionario || t.descricao_turno_funcionario || '').trim().slice(0, 50);
        if (!name || seen.has(name)) return;
        const day = WEEKDAYS.find((d) => hhmm(t[`horario_inicio_${d}`]) && hhmm(t[`horario_fim_${d}`]));
        if (!day) return;
        seen.add(name);
        out.push({
            ciclo: name,
            descricao: t.descricao_turno_funcionario ? String(t.descricao_turno_funcionario).trim() : '',
            inicio: hhmm(t[`horario_inicio_${day}`]),
            fim: hhmm(t[`horario_fim_${day}`]),
            trocaVeiculo: true,
        });
    });
    return out.sort((a, b) => a.inicio.localeCompare(b.inicio) || a.ciclo.localeCompare(b.ciclo));
}

/** Mescla parâmetros salvos (histórico) sobre os defaults, sem perder campos novos. */
export function mergeDriverParams(saved) {
    const base = defaultDriverParams();
    if (!saved || typeof saved !== 'object') return base;
    const ciclos = Array.isArray(saved.ciclos)
        ? saved.ciclos.map((c) => {
            const { multimodal, ...rest } = c || {}; // eslint-disable-line no-unused-vars
            return { ...rest, trocaVeiculo: rest.trocaVeiculo !== false };
        })
        : base.ciclos;
    const merged = {
        ...base,
        ...saved,
        ciclos,
        jornadaPorTipo: { ...(saved.jornadaPorTipo || {}) },
    };
    LEGACY_KEYS.forEach((k) => { delete merged[k]; });
    return merged;
}

function positiveNumber(v) {
    const n = Number(v);
    return v !== '' && v != null && Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Parâmetros que o worker entende (`config.motoristas`); a jornada vai em cada veículo.
 * @param {object} driverParams parâmetros do cenário
 * @param {number|string} minTransferMin tempo mínimo de transferência da roteirização
 */
export function driverParamsForJob(driverParams, minTransferMin) {
    const p = mergeDriverParams(driverParams);
    const out = {};
    ['preJornadaMin', 'posJornadaMin', 'almocoMin'].forEach((k) => {
        const n = Number(p[k]);
        if (p[k] !== '' && p[k] != null && Number.isFinite(n)) out[k] = n;
    });
    out.aplicarAlmoco = p.aplicarAlmoco !== false;
    const transfer = Number(minTransferMin);
    if (minTransferMin !== '' && minTransferMin != null && Number.isFinite(transfer) && transfer >= 0) {
        out.tempoMinimoTransferenciaMin = transfer;
    }
    out.ciclos = p.ciclos.map((c) => ({
        ciclo: String(c.ciclo || '').trim(),
        inicio: c.inicio,
        fim: c.fim,
        trocaVeiculo: c.trocaVeiculo !== false,
    }));
    return out;
}

/**
 * Corpo de POST /roteirizacao/sequenciamotoristas.
 * @param {object} args
 * @param {Array} args.routes rotas da tela (`this.routes`)
 * @param {Array} args.routingTable linhas do resumo (horários e número da rota)
 * @param {object} args.vehicleSpecById spec da frota usada na roteirização
 * @param {[number, number]|null} args.depot [lng, lat] do destino
 * @param {object} args.driverParams parâmetros do cenário
 * @param {number|string} args.minTransferMin tempo mínimo de transferência da roteirização
 * @param {(spec: object) => number|null} args.catalogShiftMinutes jornada do catálogo por spec
 */
export function buildDriverJobBody({
    routes, routingTable, vehicleSpecById, depot, driverParams, minTransferMin, catalogShiftMinutes,
}) {
    const tableByIndex = new Map();
    (routingTable || []).forEach((t) => {
        if (t && t._apiRouteIndex != null) tableByIndex.set(t._apiRouteIndex, t);
    });
    const jornadaPorTipo = (driverParams && driverParams.jornadaPorTipo) || {};
    const specs = {};
    Object.keys(vehicleSpecById || {}).forEach((id) => {
        const spec = vehicleSpecById[id] || {};
        const override = spec.typeKey != null ? positiveNumber(jornadaPorTipo[String(spec.typeKey)]) : null;
        const catalog = catalogShiftMinutes ? positiveNumber(catalogShiftMinutes(spec)) : null;
        specs[id] = {
            type: spec.type,
            capacity: spec.capacity,
            laborCost: spec.laborCost,
            laborBrlOneLeg: spec.laborBrlOneLeg,
            physicalVehicleId: spec.physicalVehicleId,
            driverShiftMinutes: override || catalog || JORNADA_LIQUIDA_PADRAO_MIN,
        };
    });
    const out = (routes || []).map((r) => {
        const row = tableByIndex.get(r._apiRouteIndex) || null;
        return {
            vehicleId: r.vehicleId != null ? r.vehicleId : null,
            routeNumber: row && row.routeNumber != null ? row.routeNumber : null,
            departureTime: row && row.departureTime ? row.departureTime : null,
            arrivalTime: row && row.arrivalTime ? row.arrivalTime : null,
            durationSeconds: Number(r.duracao != null ? r.duracao : r.durationSec) || 0,
            // Deslocamento do reaproveitado no início da rota: hora do motorista, fora do rateio.
            repositionDurationSec: Number(r.repositionDurationSec) || 0,
            sentido: Number(r.sentido) === 2 ? 2 : 1,
            startCoord: r._planStartCoord || null,
            endCoord: r._planEndCoord || null,
            _displacementLeg: !!r._displacementLeg,
            _endbaseDisplacementLeg: !!r._endbaseDisplacementLeg,
            _vehicleGroupIndex: r._vehicleGroupIndex != null ? r._vehicleGroupIndex : null,
        };
    });
    return {
        routes: out,
        vehicleSpecById: specs,
        depot: depot || null,
        params: driverParamsForJob(driverParams, minTransferMin),
    };
}

/** Só o que o resumo de custo precisa do sequenciamento. */
export function driverCostForSummary(schedule) {
    if (!schedule || !schedule.ok) return null;
    return {
        totalUnico: schedule.totalUnico,
        custoTotal: schedule.custoTotal,
        custoPorRota: schedule.custoPorRota,
        porModal: schedule.porModal,
    };
}

/** "Van|15" -> "Van - 15" (mesmo rótulo de tipo|capacidade do painel de custo). */
export function typeKeyLabel(typeKey) {
    const key = String(typeKey || '');
    const sep = key.lastIndexOf('|');
    return sep >= 0 ? `${key.slice(0, sep)} - ${key.slice(sep + 1)}` : key;
}

/** Início do dia operacional do motorista (fixo no worker). */
export const DRIVER_DAY_START_MIN = 3 * 60;

function clockToMin(raw) {
    const m = String(raw || '').match(/^(\d{1,2}):(\d{2})$/);
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/**
 * Quebra um intervalo (minutos absolutos, que podem passar de 24h) nas 24 colunas do Gantt:
 * o que passa de 24:00 volta ao início do gráfico. Devolve `[{ ini, fim }]` em 0..1440.
 */
export function wrapIntoDay(ini, fim) {
    const a = Number(ini);
    const b = Number(fim);
    if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return [];
    if (b - a >= 1440) return [{ ini: 0, fim: 1440 }];
    const start = ((a % 1440) + 1440) % 1440;
    const end = start + (b - a);
    if (end <= 1440) return [{ ini: start, fim: end }];
    return [{ ini: start, fim: 1440 }, { ini: 0, fim: end - 1440 }];
}

/** Tipos de segmento do Gantt dos motoristas, na ordem da legenda. */
export const DRIVER_SEGMENT_TYPES = ['preJornada', 'deslocamento', 'principal', 'transferencia', 'almoco', 'posJornada'];

/** Cores dos segmentos (tela e PDF usam as mesmas); `espera` é o fundo dentro da jornada. */
export const DRIVER_SEGMENT_COLORS = {
    preJornada: '#90a4ae',
    deslocamento: '#fb8c00',
    principal: '#1976d2',
    transferencia: '#8e24aa',
    almoco: '#fdd835',
    posJornada: '#546e7a',
    espera: '#eceff1',
};

/** Minutos absolutos → 'HH:mm' do relógio. */
export function minToClock(min) {
    const t = ((Math.round(min) % 1440) + 1440) % 1440;
    return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
}

/** Minutos → 'h:mm' (duração). */
export function minToDuration(min) {
    const n = Math.max(0, Math.round(Number(min) || 0));
    return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
}

/**
 * Segmentos derivados dos HH:mm, para resultado sem `linhaDoTempo` (worker anterior): pré,
 * blocos inteiros como rota principal, almoço e pós.
 */
function legacySegments(m) {
    const toAbs = (hhmmValue, ref, plusDay) => {
        let t = clockToMin(hhmmValue);
        if (t == null) return null;
        if (t < DRIVER_DAY_START_MIN) t += 1440;
        if (plusDay) t += 1440;
        while (ref != null && t < ref) t += 1440;
        return t;
    };
    const start = toAbs(m.jornada.inicio, null, false);
    if (start == null) return [];
    const segs = [];
    let cursor = start;
    (m.blocos || []).forEach((b) => {
        const ini = toAbs(b.inicio, b.dia === 'D+1' ? null : cursor, b.dia === 'D+1');
        const fim = toAbs(b.fim, ini, false);
        if (ini == null || fim == null) return;
        segs.push({ tipo: 'principal', ini, fim, rotas: b.routeNumbers || [], sentido: b.sentido });
        cursor = fim;
    });
    const end = toAbs(m.jornada.fim, cursor, false);
    const firstIni = segs.length ? segs[0].ini : cursor;
    const lastFim = segs.length ? segs[segs.length - 1].fim : cursor;
    if (firstIni > start) segs.unshift({ tipo: 'preJornada', ini: start, fim: firstIni });
    if (end != null && end > lastFim) segs.push({ tipo: 'posJornada', ini: lastFim, fim: end });
    if (m.jornada.almoco) {
        const ini = toAbs(m.jornada.almoco.inicio, start, false);
        const fim = toAbs(m.jornada.almoco.fim, ini, false);
        if (ini != null && fim != null) segs.push({ tipo: 'almoco', ini, fim });
    }
    return segs.sort((x, y) => x.ini - y.ini);
}

/**
 * Linha do tempo de um motorista em minutos absolutos do dia operacional (03:00 = 180):
 * `{ start, end, segments: [{ tipo, ini, fim, rotas?, sentido?, origem? }] }`. Usa a
 * `linhaDoTempo` do worker (blocos do dia seguinte já com +1440); sem ela, deriva dos HH:mm.
 */
export function driverTimeline(m) {
    if (!m || !m.jornada) return null;
    const segments = Array.isArray(m.linhaDoTempo) && m.linhaDoTempo.length
        ? m.linhaDoTempo.map((s) => ({ ...s, ini: Number(s.ini), fim: Number(s.fim) }))
            .filter((s) => Number.isFinite(s.ini) && Number.isFinite(s.fim) && s.fim > s.ini)
        : legacySegments(m);
    if (!segments.length) return null;
    return {
        start: Math.min(...segments.map((s) => s.ini)),
        end: Math.max(...segments.map((s) => s.fim)),
        segments,
    };
}
